/*
 * jayone-policy-toc.js
 * --------------------
 * Builds an "On this page" jump list for Shopify policy pages
 * (/policies/privacy-policy, /policies/terms-of-service).
 *
 * Why JS rather than markup in the policy text:
 *   Policy pages have no theme template — Shopify renders .shopify-policy__*
 *   itself, so Liquid can't reach inside to inject a table of contents. Writing
 *   the list into the policy body by hand would work, but it would silently rot
 *   the moment anyone edits a heading in Settings → Policies. Generating it from
 *   the headings that are actually rendered keeps it correct by construction.
 *
 * Progressive enhancement: if this never runs, the document still reads fine —
 * it just scrolls. Nothing here is load-bearing for the legal content itself.
 *
 * Only <h2> is collected. The policies use <h3> for sub-points ("a. Information
 * you give us"), and listing those would make the jump list as long as the
 * document it is meant to shorten.
 */
(function () {
  'use strict';

  /*
   * The result is used BOTH as an element id and — as '#' + id — as a CSS
   * SELECTOR by the stock theme: Accessibility.bindInPageLinks() runs
   * document.querySelector(link.hash) (assets/theme.js, unminified at
   * assets/theme.dev.js:10199).
   *
   * HTML5 lets an id start with a digit; a CSS identifier may not. So
   * '#1-information-we-collect' throws SyntaxError. The stock guard one line
   * above it uses getElementById, which accepts digit-leading ids happily, so
   * it does NOT catch this.
   *
   * The throw escapes bindInPageLinks() and aborts Accessibility.init(),
   * so clickEvents() / focusEvents() / focusEventsOff() (theme.dev.js:10046-48)
   * never run — page-wide, not just for this list. Both live policies number
   * every h2 ("1. Information we collect", … 12 and 14 of them), so this fired
   * on every /policies/* view: the TOC still scrolled (native fragment nav),
   * but focus never moved to the target heading (WCAG 2.4.3).
   *
   * Contract: never return anything that is not a valid CSS identifier.
   */
  function slugify(text, fallbackIndex) {
    var slug = text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')   // drop punctuation, keeps the leading section number
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');     // a leading '-' ahead of a digit is invalid too

    if (!slug) return 'section-' + fallbackIndex;
    // Keep the section number — just move it out of first position.
    return /^\d/.test(slug) ? 'section-' + slug : slug;
  }

  function build() {
    var container = document.querySelector('.shopify-policy__container');
    var body = document.querySelector('.shopify-policy__body');
    if (!container || !body) return;
    if (container.querySelector('.jayone-policy-toc')) return; // idempotent

    var headings = Array.prototype.slice.call(body.querySelectorAll('h2'));
    // Below ~4 sections a jump list costs more attention than it saves.
    if (headings.length < 4) return;

    var nav = document.createElement('nav');
    nav.className = 'jayone-policy-toc';
    nav.setAttribute('aria-labelledby', 'jayone-policy-toc-heading');

    var title = document.createElement('h2');
    title.className = 'jayone-policy-toc__heading';
    title.id = 'jayone-policy-toc-heading';
    title.textContent = 'On this page';
    nav.appendChild(title);

    var list = document.createElement('ul');
    list.className = 'jayone-policy-toc__list';

    headings.forEach(function (h, i) {
      var text = (h.textContent || '').trim();
      if (!text) return;
      if (!h.id) h.id = slugify(text, i + 1);

      var li = document.createElement('li');
      var a = document.createElement('a');
      a.href = '#' + h.id;
      a.textContent = text;
      li.appendChild(a);
      list.appendChild(li);
    });

    if (!list.children.length) return;
    nav.appendChild(list);

    // Sits between the page title and the legal text.
    body.parentNode.insertBefore(nav, body);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', build);
  } else {
    build();
  }
})();
